import type { Group } from "@1js/pl-types";
import React from "react";
import { useFeatureEnablement } from "../../hooks/useFeatureEnablement";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import { fetchGroups } from "../../services/prompt-client.web";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import type { HandlePromptsTabSelected } from "./AllPrompts.web.data";

export type UseWorkgroupProps = {
  handlePromptsTabSelected: HandlePromptsTabSelected;
  isWorkgroupTabShowing?: boolean;
};

export const useWorkgroups = ({
  handlePromptsTabSelected,
  isWorkgroupTabShowing = false,
}: UseWorkgroupProps) => {
  const telemetry = useTelemetry();
  const { state, dispatch } = React.useContext(LibraryContext);
  const { isWorkgroupPublishingEnabled } = useFeatureEnablement();
  const fetchGroupsOwnedAsync = React.useCallback(async () => {
    if (
      state.serviceHost &&
      state.locale &&
      !state.groupsOwnedInit &&
      isWorkgroupPublishingEnabled
    ) {
      try {
        dispatch({ type: "REQUEST_ALL_GROUPS_OWNED_INIT" });

        // resolve all requests
        const groups = await fetchGroups(
          state.serviceHost,
          state.locale,
          true,
          telemetry.sendApiActionEvent
        );

        dispatch({
          type: "REQUEST_ALL_GROUPS_OWNED_SUCCEED",
          payload: { groups: [...groups.Workgroups] },
        });
      } catch (error) {
        console.error("Error fetching owned groups:", error);
        dispatch({ type: "REQUEST_ALL_GROUPS_OWNED_FAILED" });
      }
    }
    // disable exhaustive deps because we don't want to refetch prompts when promptSettings changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.groupsOwnedInit, state.serviceHost, state.locale]);

  const fetchGroupsHasPromptsAsync: () => Promise<Group[] | undefined> =
    React.useCallback(async () => {
      if (
        state.serviceHost &&
        state.locale &&
        !state.groupsHasPromptsInit &&
        isWorkgroupPublishingEnabled
      ) {
        try {
          dispatch({ type: "REQUEST_ALL_GROUPS_HASPROMPTS_INIT" });

          // resolve all requests
          const groups = await fetchGroups(
            state.serviceHost,
            state.locale,
            false,
            telemetry.sendApiActionEvent
          );

          dispatch({
            type: "REQUEST_ALL_GROUPS_HASPROMPTS_SUCCEED",
            payload: { groups: [...groups.Workgroups] },
          });

          return groups.Workgroups;
        } catch (error) {
          console.error("Error fetching groups with prompts:", error);
          dispatch({ type: "REQUEST_ALL_GROUPS_HASPROMPTS_FAILED" });
          return undefined;
        }
      }
      // disable exhaustive deps because we don't want to refetch prompts when promptSettings changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.groupsHasPromptsInit, state.serviceHost, state.locale]);

  React.useEffect(() => {
    fetchGroupsOwnedAsync();
  }, [fetchGroupsOwnedAsync]);

  React.useEffect(() => {
    const getGroups = async () => {
      if (
        isWorkgroupPublishingEnabled &&
        state.groupHasPromptsStatus === "idle"
      ) {
        const groups = await fetchGroupsHasPromptsAsync();
        if (state.selectedTab === "GroupPrompts" && !isWorkgroupTabShowing) {
          // state.showGroupPromptsOnly means that we should display the Workgroup tab
          // but it does not guarantee that the Workgroup tab is currently selected.
          const sortedGroups = groups?.sort((a: Group, b: Group) =>
            a.DisplayName.localeCompare(b.DisplayName)
          );
          handlePromptsTabSelected("GroupPrompts", sortedGroups);
        }
      }
    };
    getGroups();
  }, [
    dispatch,
    state.selectedTab,
    state.groups,
    state.groupHasPromptsStatus,
    fetchGroupsHasPromptsAsync,
    handlePromptsTabSelected,
    isWorkgroupTabShowing,
    isWorkgroupPublishingEnabled,
  ]);

  return {
    fetchGroupsOwnedAsync,
    fetchGroupsHasPromptsAsync,
  };
};
